import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import AdminSeasonMatrix from "@/components/admin/AdminSeasonMatrix";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Trash2, ToggleLeft, ToggleRight, Edit } from "lucide-react";

// Iter 26 (Msg 658): rotas fixas com pricing por Zona × Temporada.

export default function FixedRoutes() {
  const [routes, setRoutes] = useState([]);
  const [cats, setCats] = useState([]);
  const [zones, setZones] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/fixed-routes"),
    api.get("/admin/vehicle-categories"),
    api.get("/admin/zones?active=true"),
    api.get("/admin/seasons?active=true"),
  ]).then(([r, c, z, s]) => {
    setRoutes(r.data || []);
    setCats((c.data || []).filter((x) => x.active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    setZones(z.data || []);
    setSeasons(s.data || []);
  });
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try { await api.patch(`/admin/fixed-routes/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Eliminar rota ${r.origin} → ${r.destination}?`)) return;
    try { await api.delete(`/admin/fixed-routes/${r.id}`); toast.success("Rota removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const priceFor = (r, k) => {
    const v = r.category_prices?.[k];
    if (v != null && v !== "") return Number(v);
    if (k === "economy" || k === "executive") return r.price_sedan || 0;
    if (k === "van") return r.price_van || 0;
    if (k === "minibus") return r.price_minibus || 0;
    return 0;
  };

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader
        eyebrow="Pricing · Static"
        title="Rotas Fixas"
        description="Preços por categoria de veículo com suporte a variações sazonais e zonais. Parceiros podem sobrescrever no portal deles."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="new-route-btn">
          <Plus className="w-4 h-4" /> Nova rota
        </GoldButton>
      </PageHeader>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 label-caps text-left">
              <th className="p-4">Origem</th>
              <th className="p-4">Destino</th>
              <th className="p-4">Zona</th>
              {cats.map((c) => (
                <th key={c.key} className="p-4 text-right">{c.label}</th>
              ))}
              <th className="p-4">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {routes.length === 0 && (
              <tr><td colSpan={cats.length + 5} className="p-10 text-center text-slate-500 text-sm">Sem rotas fixas.</td></tr>
            )}
            {routes.map((r) => (
              <tr key={r.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`route-row-${r.id}`}>
                <td className="p-4 text-[#0a0f1e] font-semibold">{r.origin}</td>
                <td className="p-4 text-slate-700">{r.destination}</td>
                <td className="p-4 text-[11px] uppercase tracking-widest text-[#f59e0b] font-bold">{r.zone || "—"}</td>
                {cats.map((c) => (
                  <td key={c.key} className="p-4 text-right font-mono text-[#0a0f1e]">
                    {priceFor(r, c.key) > 0 ? `€${priceFor(r, c.key).toFixed(0)}` : <span className="text-slate-400">—</span>}
                  </td>
                ))}
                <td className="p-4">
                  <button onClick={() => toggle(r)} className="flex items-center gap-1 text-xs" data-testid={`toggle-route-${r.id}`}>
                    {r.active ? <ToggleRight className="w-5 h-5 text-[#f59e0b]" /> : <ToggleLeft className="w-5 h-5 text-slate-500" />}
                    <span className={r.active ? "text-[#f59e0b]" : "text-slate-500"}>{r.active ? "Ativa" : "Inativa"}</span>
                  </button>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(r); setShow(true); }} className="text-slate-500 hover:text-[#f59e0b]" data-testid={`edit-route-${r.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(r)} className="text-slate-500 hover:text-red-500" data-testid={`del-route-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <RouteModal
          cats={cats}
          zones={zones}
          seasons={seasons}
          initial={editing}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RouteModal({ cats, zones, seasons, initial, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    origin: initial?.origin || "",
    destination: initial?.destination || "",
    zone: initial?.zone || (zones[0]?.key || "lisboa"),
    category_prices: initial?.category_prices || {},
    season_prices: initial?.season_prices || {},
  }));
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const cp = {};
      for (const [k, v] of Object.entries(f.category_prices)) {
        const n = Number(v);
        if (!isNaN(n) && n > 0) cp[k] = n;
      }
      const payload = { origin: f.origin, destination: f.destination, zone: f.zone, category_prices: cp, season_prices: f.season_prices };
      if (initial?.id) {
        await api.patch(`/admin/fixed-routes/${initial.id}`, payload);
        toast.success("Rota actualizada");
      } else {
        await api.post("/admin/fixed-routes", payload);
        toast.success("Rota criada");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6" data-testid="route-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{initial ? "Editar rota" : "Nova rota fixa"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-caps block mb-2">Origem</label>
              <Input required value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })} className="input-dt" data-testid="route-origin" placeholder="Ex: Aeroporto Lisboa (LIS)" />
            </div>
            <div>
              <label className="label-caps block mb-2">Destino</label>
              <Input required value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} className="input-dt" data-testid="route-destination" placeholder="Ex: Sintra" />
            </div>
            <div>
              <label className="label-caps block mb-2">Zona</label>
              <select value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} className="input-dt w-full" data-testid="route-zone">
                {zones.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <div className="label-caps mb-3">Preço base por categoria (€)</div>
            <div className="grid grid-cols-2 gap-3">
              {cats.map((c) => (
                <div key={c.key} className="border border-slate-200 p-3 bg-slate-50/40">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="font-semibold text-sm text-[#0a0f1e]">{c.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{c.capacity_pax}p · {c.capacity_bags} bags</div>
                  </div>
                  <Input
                    type="number" min="0" step="1"
                    value={f.category_prices[c.key] ?? ""}
                    onChange={(e) => setF({ ...f, category_prices: { ...f.category_prices, [c.key]: e.target.value } })}
                    className="input-dt !h-9"
                    placeholder="—"
                    data-testid={`route-price-${c.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-slate-500">
              Deixe vazio se esta categoria não estiver disponível para esta rota.
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <div className="label-caps mb-3">Preços por temporada (opcional)</div>
            <AdminSeasonMatrix
              seasons={seasons}
              categories={cats}
              zoneKey={f.zone}
              seasonPrices={f.season_prices}
              onChange={(sp) => setF({ ...f, season_prices: sp })}
              basePrices={f.category_prices}
              testIdPrefix="adm-route-season"
            />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="save-route">
            {loading ? "A guardar…" : (initial ? "Actualizar" : "Criar rota")}
          </GoldButton>
        </div>
      </form>
    </div>
  );
}
